import type { ILearningObjectData } from '@/entities';

export * from './types';

import type { ILearningObjectResponse, IncludedType, IPrimeDefaultObject, IPrimeResponse } from '@/services';
import type { AxiosInstance, AxiosResponse } from 'axios';
import { mapPrimeLearningObject } from '@/mappers';
import { guards } from '@/services';
import type { IEnrollmentResponse } from '@/services/prime/enrollment';

export type LearningObjectsRequestParams = {
  page: number;
  pageSize?: number;
  cursor?: string;
  tag?: string;
  query?: string;
  loTypes?: string[];
  learnerState?: string[];
  sort?: string;
};

const courseAndProgramFullInclude =
  'instances,enrollment,subLOs,subLOs.enrollment,subLOs.instances,instances.loResources,subLOs.instances.loResources,enrollment.loResourceGrades,enrollment.loInstance.subLoInstances.learningObject.enrollment.loResourceGrades';

export class PrimeLearningObjectEndpoint {
  axios: AxiosInstance;
  contentLanguage: string | undefined;
  config: ImportMetaEnv;

  constructor(axiosInstance: AxiosInstance, config: ImportMetaEnv) {
    this.axios = axiosInstance;
    this.config = config;
  }

  locale(locale: string) {
    this.contentLanguage = locale;
  }

  async fetchLearningObjectById(id: string) {
    const response: AxiosResponse<IPrimeResponse<ILearningObjectResponse>> = await this.axios
      .get(`learningObjects/${id}`, {
        params: {
          include: courseAndProgramFullInclude
        }
      })
      .catch(() => Promise.reject());

    if (response?.data.data) {
      return mapPrimeLearningObject(response.data.data, response.data.included);
    }

    return Promise.reject();
  }

  async enrollToLearningObject(id: string, instanceId: string) {
    return this.axios
      .post(`/enrollments?loId=${id}&loInstanceId=${instanceId}`)
      .then((response: AxiosResponse<IPrimeResponse<IEnrollmentResponse>>) => !!response.data.data)
      .catch(() => false);
  }

  async fetchHighlightedLearningObjects({ page, cursor, pageSize = 9 }: LearningObjectsRequestParams) {
    return this.sendRequest(`learningObjects`, {
      'page[limit]': pageSize,
      'page[cursor]': cursor ? `${cursor};${pageSize * (page - 1)}` : '',
      'filter.ignoreHigherOrderLOEnrollment': true,
      'filter.loTypes': 'course,learningProgram,certification',
      'filter.catalogIds': this.config.NV_HIGHLIGHTED_CATALOG_ID,
      sort: 'name',
      include: courseAndProgramFullInclude
    });
  }

  async fetchInProgressLearningObjects({ page, cursor, pageSize = 9 }: LearningObjectsRequestParams) {
    return this.sendRequest(`learningObjects`, {
      'page[limit]': pageSize,
      'page[cursor]': cursor ? `${cursor};${pageSize * (page - 1)}` : '',
      'filter.ignoreHigherOrderLOEnrollment': true,
      'filter.loTypes': 'course,learningProgram',
      'filter.learnerState': 'enrolled,started',
      sort: 'name',
      include: courseAndProgramFullInclude
    });
  }

  async fetchCompletedLearningObjects({ page, cursor, pageSize = 9 }: LearningObjectsRequestParams) {
    return this.sendRequest(`learningObjects`, {
      'page[limit]': pageSize,
      'page[cursor]': cursor ? `${cursor};${pageSize * (page - 1)}` : '',
      'filter.ignoreHigherOrderLOEnrollment': true,
      'filter.loTypes': 'course,learningProgram',
      'filter.learnerState': 'completed',
      sort: '-dateEnrolled',
      include: courseAndProgramFullInclude
    });
  }

  async fetchCompletedLearningObjectsCount() {
    const coursesCompleted = (
      await this.sendRequest(`learningObjects`, {
        'filter.ignoreHigherOrderLOEnrollment': true,
        'filter.loTypes': 'course',
        'filter.learnerState': 'completed'
      })
    ).total;
    const programsCompleted = (
      await this.sendRequest(`learningObjects`, {
        'filter.ignoreHigherOrderLOEnrollment': true,
        'filter.loTypes': 'learningProgram',
        'filter.learnerState': 'completed'
      })
    ).total;

    return {
      coursesCompleted: coursesCompleted || 0,
      programsCompleted: programsCompleted || 0
    };
  }

  async fetchLastCompletedCourse() {
    return this.sendRequest(`learningObjects`, {
      'page[limit]': 1,
      'filter.ignoreHigherOrderLOEnrollment': true,
      'filter.loTypes': 'course',
      'filter.learnerState': 'completed',
      sort: '-dateEnrolled',
      include: 'enrollment'
    });
  }

  async fetchLearningObjectsByTag({
    tag = '',
    page,
    cursor,
    pageSize = 9,
    sort = 'name'
  }: LearningObjectsRequestParams) {
    return this.sendRequest(`learningObjects`, {
      'page[limit]': pageSize,
      'page[cursor]': cursor ? `${cursor};${pageSize * (page - 1)}` : '',
      'filter.ignoreHigherOrderLOEnrollment': true,
      'filter.loTypes': 'course',
      'filter.learnerState': 'notenrolled,enrolled,completed,started',
      'filter.tagName': tag,
      sort,
      include: 'instances,enrollment,instances.loResources,enrollment.loResourceGrades'
    });
  }

  async fetchLearningPrograms({ page, cursor, pageSize = 9, sort = 'name' }: LearningObjectsRequestParams) {
    return this.sendRequest(`learningObjects`, {
      'page[limit]': pageSize,
      'page[cursor]': cursor ? `${cursor};${pageSize * (page - 1)}` : '',
      'filter.ignoreHigherOrderLOEnrollment': true,
      'filter.loTypes': 'learningProgram',
      'filter.learnerState': 'notenrolled,enrolled,completed,started',
      sort,
      include: courseAndProgramFullInclude
    });
  }

  async searchLearningObjects({
    page,
    cursor,
    pageSize = 9,
    query = '',
    loTypes = [],
    learnerState = [],
    sort = 'name'
  }: LearningObjectsRequestParams) {
    return this.sendRequest(`search`, {
      'page[limit]': pageSize,
      'page[cursor]': cursor ? `${cursor};${pageSize * (page - 1)}` : '',
      'filter.snippetTypes':
        'courseName,courseDescription,courseOverview,courseTag,skillDescription,lpName,lpDescription,lpOverview',
      'filter.ignoreHigherOrderLOEnrollment': true,
      'filter.loTypes': loTypes.join(',') || 'course,learningProgram',
      'filter.learnerState': learnerState.join(',') || 'notenrolled,enrolled,completed,started',
      query,
      sort,
      include: courseAndProgramFullInclude
        .split(',')
        .map((type) => `model.${type}`)
        .join(',')
    });
  }

  private async sendRequest(
    url: string,
    params: Record<string, string | number | boolean>
  ): Promise<{ data: ILearningObjectData[]; cursor?: string; total: number }> {
    const response: AxiosResponse<IPrimeResponse<ILearningObjectResponse[], IncludedType[]>> = await this.axios
      .get(url, { params: { ...params, language: this.contentLanguage } })
      .catch(() => Promise.reject());

    if (response?.data.data) {
      let cursor: string | undefined = undefined;

      if (response.data.links?.next) {
        const parsedUrl = new URL(response.data.links?.next);
        const cursorParam = parsedUrl.searchParams.get('page[cursor]');

        if (cursorParam && cursorParam.includes(';')) {
          cursor = cursorParam.split(';')[0];
        }
      }

      let noFitInLocale = 0;
      const filterLocaleFunction = (item: ILearningObjectResponse) => {
        const fitInLocale = item.attributes.localizedMetadata?.find(
          (metadata) => metadata.locale === this.contentLanguage
        );
        if (!fitInLocale) {
          noFitInLocale++;
        }
        return !this.contentLanguage || fitInLocale;
      };

      let data: ILearningObjectData[] = [];
      if (url === 'search') {
        if (response.data.included) {
          const searchResults = response.data.data;
          const learningObjects = response.data.included
            .filter((include) => guards.isLearningObject(include) && searchResults.some((lo) => lo.id === include.id))
            .map((include) => include as ILearningObjectResponse);
          const includedObjects = response.data.included.filter(
            (include) => !searchResults.some((lo) => lo.id === include.id)
          );

          data = learningObjects
            .filter(filterLocaleFunction)
            .map((data) => mapPrimeLearningObject(data, includedObjects));
        }
      } else {
        data = response.data.data
          .filter(filterLocaleFunction)
          .map((item) => mapPrimeLearningObject(item, response.data.included));
      }

      return {
        data,
        cursor,
        total:
          (response.data.meta?.count || response.data.meta?.formalCount || (params['page[limit]'] as number)) -
          noFitInLocale
      };
    }

    return Promise.reject();
  }
}
